import React from "react";
import { styled, useTheme } from "@mui/material/styles";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Chip,
  Divider,
  IconButton,
} from "@mui/material";
import {
  Close,
  Warning,
  Business,
  AccountBalance,
  LinkOff,
  CheckCircle,
} from "@mui/icons-material";
import Button from "./Button";
import SlideToConfirm from "./SlideToConfirm";
import { tierDisplayName, normalizeLevel } from "../config/entitlements";

const WarningBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.error.light,
  border: `2px solid ${theme.palette.error.main}`,
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const OrgListItem = styled(ListItem)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(0.5),
  backgroundColor: theme.palette.action.hover,
  "&:last-child": {
    marginBottom: 0,
  },
}));

/**
 * MultiSubscriptionWarningDialog
 *
 * Shows a warning before allowing multi-subscription users to access
 * the Stripe billing portal, where level changes affect ALL assigned orgs.
 *
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onClose - Called when dialog is closed without proceeding
 * @param {function} onConfirm - Called when user confirms and proceeds
 * @param {boolean} processing - Whether the confirmation is processing
 * @param {Array} assignedOrgs - List of orgs assigned to the multi-subscription
 * @param {string} subscriptionLevel - Current subscription level ("basic" or "plus")
 * @param {number} quantity - Number of subscription slots
 */
const MultiSubscriptionWarningDialog = ({
  open,
  onClose,
  onConfirm,
  processing = false,
  assignedOrgs = [],
  subscriptionLevel = "basic",
  quantity = 1,
}) => {
  const theme = useTheme();
  const isPlus = subscriptionLevel === "plus";
  const orgCount = assignedOrgs.length;

  // Count orgs with Plaid connections (for downgrade warning)
  const orgsWithPlaid = assignedOrgs.filter(
    (org) => org.hasPlaidConnections || org.plaidItemCount > 0
  );

  return (
    <Dialog
      open={open}
      onClose={processing ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Warning color="warning" />
          <Typography variant="h6" component="span">
            Multi-Subscription Notice
          </Typography>
        </Box>
        {!processing && (
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent>
        {/* Current subscription info */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Current Plan
          </Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Chip
              label={tierDisplayName(subscriptionLevel)}
              color={
                { plus: "primary", advanced: "secondary" }[
                  normalizeLevel(subscriptionLevel)
                ] || "default"
              }
              size="small"
            />
            <Typography variant="body2" color="text.secondary">
              {quantity} subscription{quantity !== 1 ? "s" : ""} &bull;{" "}
              {orgCount} organization{orgCount !== 1 ? "s" : ""} assigned
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Main warning */}
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Any plan changes will affect ALL {orgCount} organizations</strong>{" "}
            currently assigned to your multi-subscription.
          </Typography>
        </Alert>

        {/* Plus-specific downgrade warning */}
        {isPlus && (
          <WarningBox>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <LinkOff color="error" sx={{ mt: 0.5 }} />
              <Box>
                <Typography
                  variant="subtitle2"
                  color="error.dark"
                  gutterBottom
                >
                  Downgrade Warning
                </Typography>
                <Typography variant="body2" color="error.dark">
                  If you downgrade from Plus to Basic, <strong>ALL bank connections
                  will be paused</strong> for every organization after a 7-day grace period.
                  {orgsWithPlaid.length > 0 && (
                    <> This includes {orgsWithPlaid.length} organization
                    {orgsWithPlaid.length !== 1 ? "s" : ""} with active bank connections.</>
                  )}
                </Typography>
                <Typography
                  variant="body2"
                  color="error.dark"
                  sx={{ mt: 1, fontWeight: 500 }}
                >
                  Bank connections reconnect automatically if you re-subscribe, and upgrading during the grace period cancels the change entirely. </Typography>
              </Box>
            </Box>
          </WarningBox>
        )}

        {/* Affected organizations list */}
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
          Affected Organizations ({orgCount})
        </Typography>
        <List dense sx={{ maxHeight: 200, overflow: "auto" }}>
          {assignedOrgs.map((org) => (
            <OrgListItem key={org.orgId}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Business fontSize="small" color="action" />
              </ListItemIcon>
              <ListItemText
                primary={org.orgName}
                secondary={
                  isPlus && (org.hasPlaidConnections || org.plaidItemCount > 0)
                    ? "Has active bank connections"
                    : null
                }
                secondaryTypographyProps={{
                  color: "warning.main",
                  fontSize: "0.75rem",
                }}
              />
              {isPlus && (org.hasPlaidConnections || org.plaidItemCount > 0) && (
                <AccountBalance fontSize="small" color="warning" />
              )}
            </OrgListItem>
          ))}
        </List>

        <Divider sx={{ my: 2 }} />

        {/* Confirmation instructions */}
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          sx={{ mb: 2 }}
        >
          {isPlus
            ? "By proceeding, you acknowledge that downgrading will pause all bank connections after a 7-day grace period."
            : "By proceeding, you acknowledge that plan changes will affect all organizations."}
        </Typography>

        {/* Slide to confirm */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <SlideToConfirm
            onConfirm={onConfirm}
            disabled={processing}
            processing={processing}
            label="Slide to Proceed"
            processingLabel="Opening Portal..."
            width={300}
            color={isPlus ? "error" : "warning"}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={processing}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MultiSubscriptionWarningDialog;
